'use client';

import {
  makeAutoObservable,
  runInAction,
} from 'mobx';

import type { AssistantStore } from './assistantStore';
import type { AssistantAgentTab, AssistantMessage } from './assistantTypes';
import type {
  GatewayAgentInfo,
  GatewayTranscriptMessage,
} from '@/app/sdk';

import { localStorageHelper } from '@/lib/localStorageHelper';

export const STORAGE_KEY_TAB_LAYOUT_PREFIX = 'genesys-ai-assistant-tab-layout';
export const DRAFT_TAB_PREFIX = '__draft__';

export const ASSISTANT_NEW_AGENT_LABEL = 'New Agent';

export type AssistantTabDraft = {
  title?: string;
  /** Unsent composer text only — not an SDK transcript. */
  prompt?: string;
};

export type PersistedAssistantAgentTabLayout = {
  openAgentTabIds: string[];
  activeTabId: string;
  drafts?: Record<string, AssistantTabDraft>;
  /** @deprecated Migrated into `drafts` on load. */
  draftTranscripts?: Record<string, GatewayTranscriptMessage[]>;
  /** @deprecated Migrated into `drafts` on load. */
  draftTitles?: Record<string, string>;
};

export function getAgentLabel(agent: GatewayAgentInfo): string {
  const trimmed = agent.displayName.trim();
  if (!trimmed) {
    return ASSISTANT_NEW_AGENT_LABEL;
  }
  return trimmed;
}

export function isDraftAgentLabel(label: string): boolean {
  return isAutoGeneratedAgentLabel(label);
}

export function isAutoGeneratedAgentLabel(label: string): boolean {
  const normalised = label.trim().toLowerCase();
  if (!normalised) {
    return true;
  }
  return normalised === ASSISTANT_NEW_AGENT_LABEL.toLowerCase()
    || normalised.startsWith('draft:')
    || normalised.startsWith('session ')
    || normalised === 'untitled chat';
}

function createDraftTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${DRAFT_TAB_PREFIX}${crypto.randomUUID()}`;
  }
  return `${DRAFT_TAB_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function isDraftTabId(tabId: string): boolean {
  return tabId.startsWith(DRAFT_TAB_PREFIX);
}

export function getTabLayoutStorageKey(projectPath: string): string {
  return `${STORAGE_KEY_TAB_LAYOUT_PREFIX}:${projectPath}`;
}

export function isPersistedAssistantAgentTabLayout(value: unknown): value is PersistedAssistantAgentTabLayout {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const layout = value as PersistedAssistantAgentTabLayout;
  if (!Array.isArray(layout.openAgentTabIds)) {
    return false;
  }
  if (typeof layout.activeTabId !== 'string') {
    return false;
  }
  return true;
}

export function extractPromptFromLegacyTranscript(transcript: GatewayTranscriptMessage[]): string {
  for (let index = transcript.length - 1; index >= 0; index -= 1) {
    const message = transcript[index];
    if (message?.role === 'user' && typeof message.content === 'string') {
      const trimmed = message.content.trim();
      if (trimmed) {
        return trimmed;
      }
    }
  }
  return '';
}

export function mergeAssistantTabDraft(
  existing: AssistantTabDraft | undefined,
  patch: AssistantTabDraft,
): AssistantTabDraft | undefined {
  const next: AssistantTabDraft = { ...existing };
  if (patch.title !== undefined) {
    const trimmedTitle = patch.title.trim();
    if (trimmedTitle) {
      next.title = trimmedTitle;
    } else {
      delete next.title;
    }
  }
  if (patch.prompt !== undefined) {
    const trimmedPrompt = patch.prompt.trim();
    if (trimmedPrompt) {
      next.prompt = trimmedPrompt;
    } else {
      delete next.prompt;
    }
  }
  if (!next.title?.trim() && !next.prompt?.trim()) {
    return undefined;
  }
  return next;
}

export function normalisePersistedDrafts(
  layout: PersistedAssistantAgentTabLayout,
  openDraftTabIds: Set<string>,
): Record<string, AssistantTabDraft> {
  const merged: Record<string, AssistantTabDraft> = {};

  const rawDrafts = layout.drafts;
  if (rawDrafts && typeof rawDrafts === 'object' && !Array.isArray(rawDrafts)) {
    for (const [tabId, rawDraft] of Object.entries(rawDrafts)) {
      const trimmedTabId = tabId.trim();
      if (!trimmedTabId || !rawDraft || typeof rawDraft !== 'object' || Array.isArray(rawDraft)) {
        continue;
      }
      const title = typeof rawDraft.title === 'string' ? rawDraft.title.trim() : '';
      let prompt = typeof rawDraft.prompt === 'string' ? rawDraft.prompt.trim() : '';
      if (!prompt && Array.isArray((rawDraft as { transcript?: unknown }).transcript)) {
        prompt = extractPromptFromLegacyTranscript(
          (rawDraft as { transcript: GatewayTranscriptMessage[] }).transcript,
        );
      }
      const draft = mergeAssistantTabDraft(undefined, {
        ...(title ? { title } : {}),
        ...(prompt ? { prompt } : {}),
      });
      if (draft) {
        merged[trimmedTabId] = draft;
      }
    }
  }

  const legacyTranscripts = layout.draftTranscripts;
  if (legacyTranscripts && typeof legacyTranscripts === 'object' && !Array.isArray(legacyTranscripts)) {
    for (const [tabId, transcript] of Object.entries(legacyTranscripts)) {
      const trimmedTabId = tabId.trim();
      if (!trimmedTabId || !Array.isArray(transcript)) {
        continue;
      }
      const prompt = extractPromptFromLegacyTranscript(transcript as GatewayTranscriptMessage[]);
      if (!prompt) {
        continue;
      }
      const draft = mergeAssistantTabDraft(merged[trimmedTabId], { prompt });
      if (draft) {
        merged[trimmedTabId] = draft;
      }
    }
  }

  const legacyTitles = layout.draftTitles;
  if (legacyTitles && typeof legacyTitles === 'object' && !Array.isArray(legacyTitles)) {
    for (const [tabId, title] of Object.entries(legacyTitles)) {
      const trimmedTabId = tabId.trim();
      const trimmedTitle = typeof title === 'string' ? title.trim() : '';
      if (!trimmedTabId || !trimmedTitle) {
        continue;
      }
      const draft = mergeAssistantTabDraft(merged[trimmedTabId], { title: trimmedTitle });
      if (draft) {
        merged[trimmedTabId] = draft;
      }
    }
  }

  const pruned: Record<string, AssistantTabDraft> = {};
  for (const [tabId, draft] of Object.entries(merged)) {
    if (!openDraftTabIds.has(tabId)) {
      continue;
    }
    const compact = mergeAssistantTabDraft(undefined, draft);
    if (compact) {
      pruned[tabId] = compact;
    }
  }
  return pruned;
}

export function loadPersistedTabLayout(projectPath: string): PersistedAssistantAgentTabLayout | null {
  const raw = localStorageHelper.getJson<unknown>(getTabLayoutStorageKey(projectPath));
  if (!isPersistedAssistantAgentTabLayout(raw)) {
    return null;
  }
  return raw;
}

export function generateAutoAgentName(messageText: string): string {
  // Use the first non-empty line to keep names short and readable.
  const firstLine = messageText
    .split('\n')
    .map(line => line.trim())
    .find(Boolean) ?? '';
  const text = firstLine || messageText.trim();
  if (!text) return 'Untitled Chat';
  const collapsed = text.replace(/\s+/g, ' ').trim();
  const maxLength = 72;
  return collapsed.length <= maxLength ? collapsed : `${collapsed.slice(0, maxLength - 1).trimEnd()}…`;
}

export function isConversationEmpty(messages: AssistantMessage[]): boolean {
  return !messages.some(message => {
    if (message.content.trim().length > 0) {
      return true;
    }
    if ((message.thinking?.trim().length ?? 0) > 0) {
      return true;
    }
    if ((message.tools?.length ?? 0) > 0) {
      return true;
    }
    if ((message.changedFiles?.length ?? 0) > 0) {
      return true;
    }
    return false;
  });
}

export class AssistantTabsStore {
  activeTabId = '';
  openAgentTabIds: string[] = [];
  /** Maps resolved tab id → stable pane key for UI identity across draft adoption. */
  tabPaneKeyByTabId: Record<string, string> = {};
  draftByTabId: Record<string, AssistantTabDraft> = {};

  constructor(private readonly root: AssistantStore) {
    makeAutoObservable(this, {}, { autoBind: true });
    this.initializeDefaultTabs();
  }

  initializeDefaultTabs(): void {
    const initialDraftTabId = createDraftTabId();
    this.openAgentTabIds = [initialDraftTabId];
    this.activeTabId = initialDraftTabId;
    this.registerTabPaneKey(initialDraftTabId);
    this.root.conversations.getOrCreateConversation(initialDraftTabId, null);
  }

  resetTabsOnStop(): void {
    const resetDraftTabId = createDraftTabId();
    this.tabPaneKeyByTabId = {};
    this.registerTabPaneKey(resetDraftTabId);
    this.openAgentTabIds = [resetDraftTabId];
    this.activeTabId = resetDraftTabId;
    this.draftByTabId = {};
    this.root.conversations.getOrCreateConversation(resetDraftTabId, null);
  }

  clearTabPaneKeysOnProjectChange(): void {
    this.tabPaneKeyByTabId = {};
  }

  get activePaneKey(): string {
    return this.getTabPaneKey(this.getActiveTabId());
  }

  get activeHistoryAgentId(): string | null {
    return this.resolveAgentIdFromTabId(this.getActiveTabId());
  }

  get agentTabs(): AssistantAgentTab[] {
    const activePaneKey = this.activePaneKey;
    const agentsById = new Map(
      this.root.agents.map(agent => [agent.agentId, agent] as const),
    );
    const seenTabIds = new Set<string>();
    const seenPaneKeys = new Set<string>();

    return this.openAgentTabIds.flatMap((tabId) => {
      if (seenTabIds.has(tabId)) {
        return [];
      }
      seenTabIds.add(tabId);
      const paneKey = this.getTabPaneKey(tabId);
      if (seenPaneKeys.has(paneKey)) {
        return [];
      }
      seenPaneKeys.add(paneKey);
      const agentId = this.resolveAgentIdFromTabId(tabId);
      const agent = agentId ? agentsById.get(agentId) ?? null : null;
      const label = isDraftTabId(tabId)
        ? this.resolveDraftTabLabel(tabId)
        : agent
          ? getAgentLabel(agent)
          : ASSISTANT_NEW_AGENT_LABEL;

      const runningIds = this.root.conversations.runningAgentIds;
      return {
        paneKey,
        tabId,
        agentId,
        label,
        isDraft: isDraftTabId(tabId) || isDraftAgentLabel(label),
        isRunning: agentId ? (runningIds.has(agentId) || !!agent?.isRunning) : false,
        isActive: paneKey === activePaneKey,
      };
    });
  }

  getActiveTabId(): string {
    const tabId = this.activeTabId.trim();
    if (tabId && this.openAgentTabIds.includes(tabId)) {
      return tabId;
    }
    const activeAgentId = this.root.activeAgentId?.trim();
    if (activeAgentId && this.openAgentTabIds.includes(activeAgentId)) {
      return activeAgentId;
    }
    return this.openAgentTabIds[this.openAgentTabIds.length - 1] ?? '';
  }

  resolveAgentIdFromTabId(tabId: string | null | undefined): string | null {
    const trimmedTabId = tabId?.trim();
    if (!trimmedTabId || isDraftTabId(trimmedTabId)) {
      return null;
    }
    return trimmedTabId;
  }

  resolveTabIdFromPaneKey(paneKey: string): string | null {
    const trimmedPaneKey = paneKey.trim();
    if (!trimmedPaneKey) {
      return null;
    }
    for (const tabId of this.openAgentTabIds) {
      if (this.getTabPaneKey(tabId) === trimmedPaneKey) {
        return tabId;
      }
    }
    return null;
  }

  async closeAgentTab(tabId: string): Promise<void> {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !this.openAgentTabIds.includes(trimmedTabId)) {
      return;
    }

    const isActiveTab = trimmedTabId === this.getActiveTabId();
    if (
      isActiveTab
      && this.openAgentTabIds.length === 1
      && this.isTabConversationEmpty(trimmedTabId)
    ) {
      return;
    }
    const fallbackTabId = isActiveTab ? this.findNeighbourAgentTabId(trimmedTabId) : null;

    if (isDraftTabId(trimmedTabId)) {
      this.flushDraftPrompt(trimmedTabId);
    }

    runInAction(() => {
      this.openAgentTabIds = this.openAgentTabIds.filter(existingTabId => existingTabId !== trimmedTabId);
      this.removeTabPaneKey(trimmedTabId);
      if (isDraftTabId(trimmedTabId)) {
        this.deleteDraft(trimmedTabId);
        this.root.conversations.conversationsByKey.delete(trimmedTabId);
        this.root.conversations.isHydratingByKey.delete(trimmedTabId);
      }
    });

    if (!isActiveTab) {
      runInAction(() => {
        this.reconcileAgentTabs();
        this.persistTabLayout();
      });
      return;
    }

    runInAction(() => {
      this.persistTabLayout();
    });

    if (fallbackTabId) {
      await this.selectAgentTab(fallbackTabId);
      return;
    }

    const gateway = typeof window !== 'undefined' ? window.electronAPI?.gateway : null;
    if (gateway && this.root.isReady) {
      await this.root.startNewAgent();
      return;
    }

    runInAction(() => {
      this.openNewDraftTabAsOnlyTab();
      this.persistTabLayout();
    });
  }

  async selectAgentTabByPaneKey(paneKey: string): Promise<void> {
    const tabId = this.resolveTabIdFromPaneKey(paneKey);
    if (!tabId) {
      return;
    }
    await this.selectAgentTab(tabId);
  }

  async closeAgentTabByPaneKey(paneKey: string): Promise<void> {
    const tabId = this.resolveTabIdFromPaneKey(paneKey);
    if (!tabId) {
      return;
    }
    await this.closeAgentTab(tabId);
  }

  ensureAgentTabOpen(agentId: string): void {
    const trimmedAgentId = agentId.trim();
    if (!trimmedAgentId || isDraftTabId(trimmedAgentId)) {
      return;
    }
    if (this.openAgentTabIds.includes(trimmedAgentId)) {
      return;
    }
    this.registerTabPaneKey(trimmedAgentId);
    this.openAgentTabIds = [...this.openAgentTabIds, trimmedAgentId];
    this.root.conversations.getOrCreateConversation(trimmedAgentId, trimmedAgentId);
  }

  async selectAgentTab(tabId: string): Promise<void> {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId) {
      return;
    }

    if (isDraftTabId(trimmedTabId)) {
      if (this.getActiveTabId() === trimmedTabId && !this.root.activeAgentId) {
        return;
      }
      await this.switchToDraftTab(trimmedTabId);
      return;
    }

    const agentId = this.resolveAgentIdFromTabId(trimmedTabId);
    if (!agentId) {
      return;
    }

    runInAction(() => {
      this.ensureAgentTabOpen(agentId);
      this.activeTabId = agentId;
      this.persistTabLayout();
    });
    await this.root.switchAgent(agentId);
  }

  async selectAgentTabAfterActiveDelete(fallbackTabId: string | null): Promise<void> {
    if (fallbackTabId?.trim()) {
      await this.selectAgentTab(fallbackTabId);
      return;
    }

    const remainingTabId = this.openAgentTabIds[this.openAgentTabIds.length - 1]?.trim();
    if (remainingTabId) {
      await this.selectAgentTab(remainingTabId);
      return;
    }

    runInAction(() => {
      this.openNewDraftTabAsOnlyTab();
      this.persistTabLayout();
    });
  }

  reconcileAgentTabsAgainstKnownAgents(agents: GatewayAgentInfo[]): void {
    const knownAgentIds = new Set(agents.map(a => a.agentId.trim()));
    const validTabIds: string[] = [];
    for (const tabId of this.openAgentTabIds) {
      if (isDraftTabId(tabId)) {
        validTabIds.push(tabId);
        continue;
      }
      if (knownAgentIds.has(tabId) || tabId === this.root.activeAgentId?.trim()) {
        validTabIds.push(tabId);
      } else {
        this.root.conversations.conversationsByKey.delete(tabId);
        this.root.conversations.isHydratingByKey.delete(tabId);
        this.removeTabPaneKey(tabId);
      }
    }
    if (validTabIds.length === 0) {
      const fallbackDraftTabId = createDraftTabId();
      this.registerTabPaneKey(fallbackDraftTabId);
      this.root.conversations.getOrCreateConversation(fallbackDraftTabId, null);
      validTabIds.push(fallbackDraftTabId);
      this.activeTabId = fallbackDraftTabId;
      this.root.activeAgentId = null;
    }
    this.openAgentTabIds = validTabIds;
  }

  reconcileAgentTabs(
    previousActiveAgentId?: string | null,
    previousDraftTabId?: string | null,
  ): void {
    const filteredTabIds: string[] = [];
    for (const tabId of this.openAgentTabIds) {
      const trimmedTabId = tabId.trim();
      if (!trimmedTabId || filteredTabIds.includes(trimmedTabId)) {
        continue;
      }
      filteredTabIds.push(trimmedTabId);
    }

    const adoptedAgentId = this.root.activeAgentId?.trim() ?? null;
    const draftTabToReplace = previousDraftTabId && isDraftTabId(previousDraftTabId)
      ? previousDraftTabId
      : (isDraftTabId(this.activeTabId) ? this.activeTabId : null);
    const adoptedDraftTitle = draftTabToReplace
      ? this.resolveAdoptedDraftTitle(draftTabToReplace)
      : '';
    if (adoptedAgentId && draftTabToReplace) {
      const draftTabIndex = filteredTabIds.indexOf(draftTabToReplace);
      if (draftTabIndex >= 0) {
        if (!filteredTabIds.includes(adoptedAgentId)) {
          filteredTabIds[draftTabIndex] = adoptedAgentId;
          this.migrateTabPaneKey(draftTabToReplace, adoptedAgentId);
        } else {
          filteredTabIds.splice(draftTabIndex, 1);
          this.removeTabPaneKey(draftTabToReplace);
        }
        this.deleteDraft(draftTabToReplace);
      }
    }

    if (
      previousActiveAgentId !== undefined
      && previousActiveAgentId !== null
      && adoptedAgentId
      && draftTabToReplace
    ) {
      const previousAgentTabId = previousActiveAgentId.trim();
      if (
        previousAgentTabId
        && previousAgentTabId !== adoptedAgentId
        && filteredTabIds.includes(previousAgentTabId)
        && !filteredTabIds.includes(adoptedAgentId)
      ) {
        const previousTabIndex = filteredTabIds.indexOf(previousAgentTabId);
        filteredTabIds[previousTabIndex] = adoptedAgentId;
        this.migrateTabPaneKey(previousAgentTabId, adoptedAgentId);
      }
    }

    const activeTabId = this.getActiveTabId();
    if (!filteredTabIds.includes(activeTabId)) {
      filteredTabIds.push(activeTabId);
    }

    if (filteredTabIds.length === 0) {
      const fallbackDraftTabId = this.openNewDraftTabAsOnlyTab();
      filteredTabIds.push(fallbackDraftTabId);
    }

    this.openAgentTabIds = filteredTabIds;
    this.syncTabPaneKeysToOpenTabs();
    if (!this.root.activeAgentId && !filteredTabIds.includes(this.activeTabId)) {
      this.activeTabId = activeTabId;
    }
    if (adoptedAgentId && adoptedDraftTitle) {
      void this.applyDraftTitleToAgentIfReplaceable(adoptedAgentId, adoptedDraftTitle);
    }
  }

  loadTabLayoutFromStorage(projectPath: string): void {
    const layout = loadPersistedTabLayout(projectPath);
    if (!layout) {
      return;
    }

    const openTabIds = Array.isArray(layout.openAgentTabIds) ? layout.openAgentTabIds : [];
    const seenTabIds = new Set<string>();
    const tabIds = openTabIds.flatMap((tabId) => {
      const trimmedTabId = typeof tabId === 'string' ? tabId.trim() : '';
      if (!trimmedTabId || seenTabIds.has(trimmedTabId)) {
        return [];
      }
      seenTabIds.add(trimmedTabId);
      return [trimmedTabId];
    });
    if (tabIds.length === 0) {
      return;
    }

    const persistedActiveTabId =
      typeof layout.activeTabId === 'string' ? layout.activeTabId.trim() : '';
    const openDraftTabIds = new Set(
      tabIds.filter(tabId => isDraftTabId(tabId)),
    );
    const restoredDrafts = normalisePersistedDrafts(layout, openDraftTabIds);

    runInAction(() => {
      this.openAgentTabIds = tabIds;
      for (const tabId of tabIds) {
        this.registerTabPaneKey(tabId);
      }
      this.syncTabPaneKeysToOpenTabs();
      const resolvedActiveTabId = tabIds.includes(persistedActiveTabId)
        ? persistedActiveTabId
        : tabIds[tabIds.length - 1] ?? tabIds[0]!;
      this.activeTabId = resolvedActiveTabId;
      if (!isDraftTabId(resolvedActiveTabId)) {
        this.root.activeAgentId = resolvedActiveTabId;
      }
      this.draftByTabId = { ...restoredDrafts };
      for (const tabId of tabIds) {
        const agentId = isDraftTabId(tabId) ? null : tabId;
        this.root.conversations.getOrCreateConversation(tabId, agentId);
      }
    });
  }

  persistTabLayout(): void {
    const projectPath = this.root.lastProjectPath?.trim();
    if (!projectPath) {
      return;
    }
    this.syncTabPaneKeysToOpenTabs();

    localStorageHelper.setJson(getTabLayoutStorageKey(projectPath), {
      openAgentTabIds: this.openAgentTabIds,
      activeTabId: this.getActiveTabId(),
      drafts: this.buildPersistedDrafts(),
    });
  }

  getDraftPromptForTab(tabId: string): string {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId) {
      return '';
    }
    return this.draftByTabId[trimmedTabId]?.prompt?.trim() ?? '';
  }

  updateDraftPrompt(tabId: string, prompt: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !isDraftTabId(trimmedTabId) || !this.canPersistDraftPrompt(trimmedTabId)) {
      return;
    }
    runInAction(() => {
      this.upsertDraft(trimmedTabId, { prompt });
      this.persistTabLayout();
    });
  }

  flushDraftPrompt(tabId: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !isDraftTabId(trimmedTabId)) {
      return;
    }
    // Immediate persist is handled by updateDraftPrompt; this is a no-op placeholder
    // for tab-switch call sites that may pass composer text via updateDraftPrompt first.
  }

  flushActiveDraftPrompt(): void {
    this.flushDraftPrompt(this.getActiveTabId());
  }

  canEditDraftComposer(tabId: string): boolean {
    return this.canPersistDraftPrompt(tabId);
  }

  setDraftTitleFromPrompt(tabId: string, prompt: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !isDraftTabId(trimmedTabId)) {
      return;
    }
    const nextTitle = generateAutoAgentName(prompt).trim();
    if (!nextTitle) {
      return;
    }
    if ((this.draftByTabId[trimmedTabId]?.title?.trim() ?? '') === nextTitle) {
      return;
    }
    runInAction(() => {
      this.upsertDraft(trimmedTabId, { title: nextTitle });
      this.persistTabLayout();
    });
  }

  deleteDraft(tabId: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !this.draftByTabId[trimmedTabId]) {
      return;
    }
    const remaining = { ...this.draftByTabId };
    delete remaining[trimmedTabId];
    this.draftByTabId = remaining;
  }

  appendNewDraftTab(): string {
    const newDraftTabId = createDraftTabId();
    runInAction(() => {
      this.registerTabPaneKey(newDraftTabId);
      this.openAgentTabIds = [...this.openAgentTabIds, newDraftTabId];
      this.activeTabId = newDraftTabId;
      this.root.conversations.getOrCreateConversation(newDraftTabId, null);
    });
    return newDraftTabId;
  }

  openNewDraftTabAsOnlyTab(): string {
    const newDraftTabId = createDraftTabId();
    this.registerTabPaneKey(newDraftTabId);
    this.openAgentTabIds = [newDraftTabId];
    this.activeTabId = newDraftTabId;
    this.root.activeAgentId = null;
    this.root.conversations.getOrCreateConversation(newDraftTabId, null);
    return newDraftTabId;
  }

  adoptAgentTabFromGatewayEvent(
    eventAgentId: string | null,
    previousActiveAgentId: string | null,
    previousDraftTabId: string | null,
  ): void {
    this.root.activeAgentId = eventAgentId;
    if (eventAgentId) {
      this.activeTabId = eventAgentId;
      if (!this.root.conversations.conversationsByKey.has(eventAgentId)) {
        this.root.conversations.getOrCreateConversation(eventAgentId, eventAgentId);
      }
    }
    this.reconcileAgentTabs(previousActiveAgentId, previousDraftTabId);
    this.persistTabLayout();
  }

  setActiveTabIdForAgentSwitch(agentId: string): void {
    this.activeTabId = agentId;
  }

  removeAgentTab(agentId: string, fallbackTabId: string | null, isDeletingActiveAgent: boolean): void {
    this.openAgentTabIds = this.openAgentTabIds.filter(tabId => tabId !== agentId);
    this.removeTabPaneKey(agentId);
    if (isDeletingActiveAgent) {
      this.root.activeAgentId = null;
      if (fallbackTabId) {
        this.activeTabId = fallbackTabId;
      }
    }
    this.reconcileAgentTabs();
    this.persistTabLayout();
  }

  findNeighbourAgentTabId(tabId: string): string | null {
    const tabIndex = this.openAgentTabIds.indexOf(tabId);
    if (tabIndex < 0) {
      return null;
    }
    return this.openAgentTabIds[tabIndex - 1] ?? this.openAgentTabIds[tabIndex + 1] ?? null;
  }

  private getTabPaneKey(tabId: string): string {
    const trimmedTabId = tabId.trim();
    return this.tabPaneKeyByTabId[trimmedTabId] ?? trimmedTabId;
  }

  private registerTabPaneKey(tabId: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || this.tabPaneKeyByTabId[trimmedTabId]) {
      return;
    }
    this.tabPaneKeyByTabId = { ...this.tabPaneKeyByTabId, [trimmedTabId]: trimmedTabId };
  }

  private migrateTabPaneKey(fromTabId: string, toTabId: string): void {
    const fromKey = fromTabId.trim();
    const toKey = toTabId.trim();
    if (!fromKey || !toKey || fromKey === toKey) {
      return;
    }
    const paneKey = this.tabPaneKeyByTabId[fromKey] ?? fromKey;
    const next = { ...this.tabPaneKeyByTabId };
    next[toKey] = paneKey;
    delete next[fromKey];
    this.tabPaneKeyByTabId = next;
  }

  private removeTabPaneKey(tabId: string): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !(trimmedTabId in this.tabPaneKeyByTabId)) {
      return;
    }
    const next = { ...this.tabPaneKeyByTabId };
    delete next[trimmedTabId];
    this.tabPaneKeyByTabId = next;
  }

  private syncTabPaneKeysToOpenTabs(): void {
    const activeTabId = this.activeTabId.trim();
    const uniqueTabIds: string[] = [];
    const paneKeysByIndex: string[] = [];
    const paneKeyToIndex = new Map<string, number>();

    for (const rawTabId of this.openAgentTabIds) {
      const tabId = rawTabId.trim();
      if (!tabId) {
        continue;
      }
      const paneKey = this.tabPaneKeyByTabId[tabId] ?? tabId;
      const existingIndex = paneKeyToIndex.get(paneKey);
      if (existingIndex === undefined) {
        paneKeyToIndex.set(paneKey, uniqueTabIds.length);
        uniqueTabIds.push(tabId);
        paneKeysByIndex.push(paneKey);
        continue;
      }

      const existingTabId = uniqueTabIds[existingIndex] ?? '';
      if (this.shouldPreferTabForPaneKey(existingTabId, tabId, activeTabId)) {
        uniqueTabIds[existingIndex] = tabId;
      }
    }

    const next: Record<string, string> = {};
    for (let index = 0; index < uniqueTabIds.length; index += 1) {
      const tabId = uniqueTabIds[index];
      const paneKey = paneKeysByIndex[index];
      if (tabId && paneKey) {
        next[tabId] = paneKey;
      }
    }

    this.openAgentTabIds = uniqueTabIds;
    this.tabPaneKeyByTabId = next;
  }

  private shouldPreferTabForPaneKey(
    existingTabId: string,
    candidateTabId: string,
    activeTabId: string,
  ): boolean {
    const existingIsDraft = isDraftTabId(existingTabId);
    const candidateIsDraft = isDraftTabId(candidateTabId);
    if (existingIsDraft !== candidateIsDraft) {
      return !candidateIsDraft;
    }
    if (candidateTabId === activeTabId && existingTabId !== activeTabId) {
      return true;
    }
    return false;
  }

  private buildPersistedDrafts(): Record<string, AssistantTabDraft> {
    const openDraftTabIds = new Set(
      this.openAgentTabIds.filter(tabId => isDraftTabId(tabId)),
    );
    const persisted: Record<string, AssistantTabDraft> = {};
    for (const [tabId, draft] of Object.entries(this.draftByTabId)) {
      if (!openDraftTabIds.has(tabId)) {
        continue;
      }
      const compact = mergeAssistantTabDraft(undefined, draft);
      if (compact) {
        persisted[tabId] = compact;
      }
    }
    return persisted;
  }

  private upsertDraft(tabId: string, patch: AssistantTabDraft): void {
    const trimmedTabId = tabId.trim();
    if (!trimmedTabId || !isDraftTabId(trimmedTabId)) {
      return;
    }
    const nextDraft = mergeAssistantTabDraft(this.draftByTabId[trimmedTabId], patch);
    if (!nextDraft) {
      const remaining = { ...this.draftByTabId };
      delete remaining[trimmedTabId];
      this.draftByTabId = remaining;
      return;
    }
    this.draftByTabId = {
      ...this.draftByTabId,
      [trimmedTabId]: nextDraft,
    };
  }

  private canPersistDraftPrompt(tabId: string): boolean {
    if (!isDraftTabId(tabId) || this.root.conversations.isHydratingByKey.get(tabId)) {
      return false;
    }
    const rt = this.root.conversations.conversationsByKey.get(tabId);
    if (!rt) {
      return true;
    }
    return !rt.hasPendingRun && rt.assistantState !== 'running';
  }

  private resolveDraftTabLabel(tabId: string): string {
    const draftTitle = this.draftByTabId[tabId]?.title?.trim();
    if (draftTitle) {
      return draftTitle;
    }
    const rt = this.root.conversations.conversationsByKey.get(tabId);
    const firstUserMessage = rt?.messages.find((message: AssistantMessage) => message.role === 'user');
    if (firstUserMessage?.content.trim()) {
      return generateAutoAgentName(firstUserMessage.content);
    }
    return ASSISTANT_NEW_AGENT_LABEL;
  }

  private resolveAdoptedDraftTitle(draftTabId: string): string {
    const draftTitle = this.draftByTabId[draftTabId]?.title?.trim();
    if (draftTitle) {
      return draftTitle;
    }
    const rt = this.root.conversations.conversationsByKey.get(draftTabId);
    const firstUserMessage = rt?.messages.find((message: AssistantMessage) => message.role === 'user');
    if (firstUserMessage?.content.trim()) {
      return generateAutoAgentName(firstUserMessage.content);
    }
    return '';
  }

  private isTabConversationEmpty(tabId: string): boolean {
    if (isDraftTabId(tabId) && (this.draftByTabId[tabId]?.prompt?.trim() ?? '').length > 0) {
      return false;
    }
    const rt = this.root.conversations.conversationsByKey.get(tabId);
    if (rt) {
      return isConversationEmpty(rt.messages);
    }
    return true;
  }

  private async applyDraftTitleToAgentIfReplaceable(agentId: string, title: string): Promise<void> {
    const trimmedAgentId = agentId.trim();
    const trimmedTitle = title.trim();
    if (!trimmedAgentId || !trimmedTitle) {
      return;
    }

    const existingAgent = this.root.agents.find(agent => agent.agentId === trimmedAgentId);
    if (existingAgent && !isAutoGeneratedAgentLabel(existingAgent.displayName)) {
      return;
    }

    runInAction(() => {
      this.root.upsertLocalAgent(trimmedAgentId, trimmedTitle);
    });

    const gateway = window.electronAPI?.gateway;
    if (!gateway) {
      return;
    }
    try {
      await gateway.renameAgent(trimmedAgentId, trimmedTitle);
    } catch {
      // Best-effort migration only.
    }
  }

  private async switchToDraftTab(draftTabId: string): Promise<void> {
    if (this.root.isAgentTransitioning) {
      return;
    }

    this.flushActiveDraftPrompt();

    if (this.root.activeAgentId) {
      const gateway = window.electronAPI?.gateway;
      if (!gateway) {
        return;
      }
      const transitionToken = this.root.beginAgentTransition(null);
      try {
        const settings = await gateway.startNewAgent(this.root.lastProjectPath ?? undefined);
        if (!this.root.isCurrentAgentTransition(transitionToken)) {
          return;
        }

        runInAction(() => {
          this.root.activeAgentId = null;
          this.root.applySettings(settings);
          this.activeTabId = draftTabId;
          this.root.conversations.getOrCreateConversation(draftTabId, null);
          this.root.approvals.clearApprovals();
          this.reconcileAgentTabs();
          this.persistTabLayout();
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        runInAction(() => {
          this.root.errorMessage = message;
          this.root.lastErrorKind = 'startup';
        });
      } finally {
        this.root.endAgentTransition(transitionToken);
      }
      return;
    }

    runInAction(() => {
      this.root.activeAgentId = null;
      this.activeTabId = draftTabId;
      this.root.conversations.getOrCreateConversation(draftTabId, null);
      this.persistTabLayout();
    });
  }
}
